/**
 * Navigation guards.
 *
 * Three outcomes, and each one is a real navigation rather than a rendered
 * placeholder, so `router.spec.ts` can assert on the destination:
 *
 *   - **not signed in, screen needs a role** → `/login?redirect=<path>`. The
 *     original destination survives the round trip.
 *   - **signed in, wrong role** → `/forbidden?from=<path>`. Not a silent bounce
 *     to home: a user who followed a colleague's link deserves to be told the
 *     link is for a different role rather than to wonder why they landed on a
 *     dashboard.
 *   - **signed in, on a signed-out-only screen** → their role's home. This is
 *     `ACC-03`'s role-aware redirect.
 *
 * None of this is security. The server decides what to return (I-3); these
 * guards decide what to render.
 */

import type { Router } from 'vue-router'

import { SCREENS_BY_ID, roleMayView } from '@/screens/catalog'
import type { useSessionStore } from '@/stores/session'

type SessionStore = ReturnType<typeof useSessionStore>

export function installGuards (router: Router, session: SessionStore): void {
  router.beforeEach(async to => {
    // One await, once. A guard that re-restores on every navigation would make
    // every route transition wait on storage.
    if (session.status === 'unknown') await session.bootstrap()

    const screen = SCREENS_BY_ID[to.meta.screenId]
    const access = screen.access

    if (access.kind === 'anonymous') return true

    if (access.kind === 'anonymous-only') {
      return session.isAuthenticated ? { path: session.roleHomePath, replace: true } : true
    }

    if (!session.isAuthenticated) {
      return { name: 'login', query: { redirect: to.fullPath }, replace: true }
    }

    if (!roleMayView(screen, session.role)) {
      return { name: 'forbidden', query: { from: to.fullPath }, replace: true }
    }

    return true
  })

  router.afterEach(to => {
    if (typeof document === 'undefined') return
    // Announced by screen readers on navigation, which an SPA otherwise does not
    // do at all. The heading focus move in the layouts is the other half.
    document.title = `${to.meta.title} · Match Point`
  })
}
